// The import flow does a hard `window.location.href` redirect to /product
// right after import, which destroys the Redux store before a directly
// dispatched notification could ever render. The pending notification(s)
// are stashed here instead, and replayed once by Product.js on mount.
export const IMPORT_NOTIFICATIONS_STORAGE_KEY =
	'catalogImportPendingNotifications';

const NAME_SYNONYMS = ['nom', 'name', 'produit', 'designation', 'article', 'libelle'];
const PRICE_SYNONYMS = ['prix', 'price', 'tarif', 'montant', 'pu', 'pvttc', 'pv'];
// Attention — « Prix d'achat » contient « prix » : ces deux listes se
// chevauchent volontairement, et c'est detectPriceColumn qui démêle (voir
// son commentaire). Les deux graphies de l'apostrophe sont listées parce que
// normalize() ne les rapproche pas : un en-tête tapé dans Google Sheets
// porte souvent l'apostrophe typographique.
const PURCHASE_PRICE_SYNONYMS = [
	"prix d'achat", 'prix d’achat', 'prix d achat', 'prix achat', 'prix dachat',
	'achat', 'purchase price', 'cost', 'pa',
];
// Aucun synonyme existant ne répond à ces en-têtes, et ceux-ci ne répondent
// à aucun en-tête existant (couvert par un test) : pas de démêlage à la
// detectPriceColumn à prévoir. « Qté » se normalise en « qte ».
const STOCK_SYNONYMS = ['stock', 'quantite en stock', 'qte stock'];
const STATUS_SEND_SYNONYMS = ['suite', "temps d'envoi", 'temps denvoi'];
// Unlike type/tax rate/place send/color, a category that doesn't match an
// existing one is a normal, expected outcome (it gets created) — not a
// silent fallback-to-default. Requiring every cell to match a known name
// (like detectColumnByValues does for those) would mean this column could
// never auto-detect on an account with no categories yet, which is the
// common case. Detected by header synonym instead, like Nom/Prix/Suite.
const CATEGORY_SYNONYMS = ['categorie', 'category', 'famille', 'classification'];

// Type/tax rate/place send/color DO fall back to a default when a value
// doesn't match (unlike category) — but a column literally named "Type",
// "Lieu d'envoi", etc. is still an unambiguous, safe signal on its own.
// Tried as a fallback in ImportCatalog.js when detectColumnByValues (every
// cell must match a known name) doesn't find anything, so a column can
// still auto-detect even if a few of its values don't match existing
// entries (those rows just fall back to the default per-row, as always).
const TYPE_SYNONYMS = ['type'];
const TAX_RATE_SYNONYMS = ['taux de tva', 'tva', 'taux tva', 'tax rate', 'vat'];
const PLACE_SEND_SYNONYMS = [
	"lieu d'envoi", 'lieu envoi', 'point de fabrication', 'place send',
];
const COLOR_SYNONYMS = ['couleur', 'color'];
const ID_SYNONYMS = ['id', 'identifiant', 'id produit'];

// Nicely-cased/accented versions of the synonym lists above, for display
// only (as a hint to the user before they build their file) — matching
// itself always goes through normalize() against the lists above.
export const NAME_SYNONYMS_DISPLAY = [
	'Nom', 'Name', 'Produit', 'Désignation', 'Article', 'Libellé',
];
export const PRICE_SYNONYMS_DISPLAY = [
	'Prix', 'Price', 'Tarif', 'Montant', 'PU', 'PVTTC', 'PV',
];
export const STATUS_SEND_SYNONYMS_DISPLAY = ['Suite', "Temps d'envoi"];
export const CATEGORY_SYNONYMS_DISPLAY = [
	'Catégorie', 'Category', 'Famille', 'Classification',
];
export const TYPE_SYNONYMS_DISPLAY = ['Type'];
export const TAX_RATE_SYNONYMS_DISPLAY = ['Taux de TVA', 'TVA', 'Tax rate'];
export const PLACE_SEND_SYNONYMS_DISPLAY = [
	"Lieu d'envoi", 'Point de fabrication', 'Place send',
];
export const COLOR_SYNONYMS_DISPLAY = ['Couleur', 'Color'];
export const ID_SYNONYMS_DISPLAY = ['ID', 'Identifiant', 'Id produit'];
// Le nom canonique en tête — c'est celui que porte le modèle téléchargé et
// celui qu'emploie l'onglet Quantités. Les autres restent reconnus.
export const PURCHASE_PRICE_SYNONYMS_DISPLAY = [
	"Prix d'achat HT", "Prix d'achat", 'Achat', 'PA',
];
export const STOCK_SYNONYMS_DISPLAY = ['Stock', 'Quantité en stock', 'Qté stock'];

// "Suite d'envoi" isn't backed by a database table (unlike type/tax
// rate/place send/color) — it's a fixed enum, so its accepted cell values
// are shown as a static example list rather than derived from any entries.
export const STATUS_SEND_VALUES_DISPLAY = 'Direct, 1, 2, 3, ...';

const DRINK_KEYWORDS = [
	'biere', 'biere pression', 'panache', 'vin', 'rose', 'champagne', 'cocktail',
	'mojito', 'spritz', 'whisky', 'vodka', 'rhum', 'gin', 'pastis', 'aperitif',
	'digestif', 'cidre', 'kir', 'soda', 'coca', 'cola', 'fanta', 'sprite',
	'ice tea', 'the glace', 'jus', 'cafe', 'expresso', 'espresso', 'cappuccino',
	'cappucino', 'the', 'chocolat chaud', 'eau', 'perrier', 'evian', 'limonade',
	'sirop', 'milkshake', 'smoothie', 'caipirinha', 'campari', 'canette',
];

// A name containing a volume with a beverage unit (33cl, 1,5L, 50 cl...) is
// a very reliable drink signal on a restaurant menu — food items aren't
// named with a volume. Checked independently of the keyword list.
const VOLUME_UNIT_PATTERN = /\d+(?:[.,]\d+)?\s*(cl|centilitres?|litres?|l)\b/;

function normalize(str) {
	return str
		.toString()
		.trim()
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '');
}

function escapeRegExp(str) {
	return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function matchingColumns(headers, synonyms) {
	const matches = [];
	headers.forEach((header, index) => {
		const normalizedHeader = normalize(header);
		const isMatch = synonyms.some(synonym => {
			const pattern = new RegExp(`\\b${escapeRegExp(synonym)}\\b`);
			return pattern.test(normalizedHeader);
		});
		if (isMatch) {
			matches.push(index);
		}
	});
	return matches;
}

function detectColumn(headers, synonyms) {
	const matches = matchingColumns(headers, synonyms);
	return matches.length === 1 ? matches[0] : null;
}

export function detectNameColumn(headers) {
	return detectColumn(headers, NAME_SYNONYMS);
}

/**
 * Le prix de **vente**. « Prix d'achat » contient « prix » : sans écarter
 * d'abord les colonnes d'achat, un fichier portant les deux offrirait deux
 * candidats au prix de vente, detectColumn rendrait null faute de savoir
 * trancher, et la détection automatique du prix — jusque-là fiable —
 * casserait sur tout fichier qui adopte la nouvelle colonne facultative.
 * @param {Array<String>} headers
 * @returns {Number|null}
 */
export function detectPriceColumn(headers) {
	const purchaseIndexes = matchingColumns(headers, PURCHASE_PRICE_SYNONYMS);
	const candidateHeaders = purchaseIndexes.length
		? headers.map((header, index) =>
				purchaseIndexes.includes(index) ? '' : header
			)
		: headers;
	return detectColumn(candidateHeaders, PRICE_SYNONYMS);
}

export function detectPurchasePriceColumn(headers) {
	return detectColumn(headers, PURCHASE_PRICE_SYNONYMS);
}

export function detectStockColumn(headers) {
	return detectColumn(headers, STOCK_SYNONYMS);
}

export function detectStatusSendColumn(headers) {
	return detectColumn(headers, STATUS_SEND_SYNONYMS);
}

export function detectCategoryColumn(headers) {
	return detectColumn(headers, CATEGORY_SYNONYMS);
}

export function detectTypeColumnByHeader(headers) {
	return detectColumn(headers, TYPE_SYNONYMS);
}

export function detectTaxRateColumnByHeader(headers) {
	return detectColumn(headers, TAX_RATE_SYNONYMS);
}

export function detectPlaceSendColumnByHeader(headers) {
	return detectColumn(headers, PLACE_SEND_SYNONYMS);
}

export function detectColorColumnByHeader(headers) {
	return detectColumn(headers, COLOR_SYNONYMS);
}

export function detectIdColumn(headers) {
	return detectColumn(headers, ID_SYNONYMS);
}

/**
 * "Suite d'envoi" isn't backed by a DB table (unlike type/tax rate/place
 * send/color) — it's a fixed enum: 'Direct' -> 0, or a plain non-negative
 * integer string/number -> that same integer. Mirrors the server-side
 * parseStatusSendIndex in carte-web-server/inc/dao/ProductCatalogImport.js.
 * @param {*} rawValue
 * @returns {Number|undefined} undefined if not recognized
 */
export function parseStatusSendIndex(rawValue) {
	if (rawValue === undefined || rawValue === null || rawValue.toString().trim() === '') {
		return undefined;
	}
	if (typeof rawValue === 'number') {
		return Number.isInteger(rawValue) && rawValue >= 0 ? rawValue : undefined;
	}
	const normalized = normalize(rawValue);
	if (normalized === 'direct') {
		return 0;
	}
	return /^\d+$/.test(normalized) ? parseInt(normalized, 10) : undefined;
}

/**
 * Le prix d'achat HT d'une ligne, et le verdict qui va avec.
 *
 * Cellule vide **et** 0 veulent dire la même chose — « ne rien faire » — et
 * surtout pas « effacer le prix d'achat de ce produit » : la colonne est
 * facultative jusque dans ses valeurs, et l'utilisateur ne renseigne que les
 * lignes qui l'intéressent. Un négatif ou une valeur illisible, en revanche,
 * est une faute de saisie : `invalid` la remonte pour que l'écran refuse la
 * ligne, au même régime que le prix de vente, plutôt que de l'ignorer en
 * silence.
 * @param {*} rawValue
 * @returns {{value: Number|null, invalid: Boolean}} `value` arrondi au
 *   centime, ou null quand il n'y a rien à écrire
 */
export function parsePurchasePrice(rawValue) {
	if (
		rawValue === undefined ||
		rawValue === null ||
		rawValue.toString().trim() === ''
	) {
		return { value: null, invalid: false };
	}
	const parsed = parsePrice(rawValue);
	if (parsed === null || parsed < 0) {
		return { value: null, invalid: true };
	}
	if (parsed === 0) {
		return { value: null, invalid: false };
	}
	return { value: Math.round(parsed * 100) / 100, invalid: false };
}

/**
 * Le stock d'une ligne, et le verdict qui va avec.
 *
 * Cellule vide = « ne pas toucher au stock » : ni activation du suivi, ni
 * mouvement. Contrairement au prix d'achat, 0 est ici une vraie valeur — un
 * produit épuisé — et part donc au serveur. Seul un entier positif ou nul est
 * accepté : un négatif, un décimal ou une valeur illisible est une faute de
 * saisie que l'écran refuse ligne par ligne, au même régime que le prix
 * d'achat. « 12,0 » est accepté : c'est l'entier 12 écrit par un tableur.
 * Une date (cellule reformatée par le tableur) tombe elle aussi en `invalid`
 * — l'écran la repère avant pour lui donner son propre message.
 * @param {*} rawValue
 * @returns {{value: Number|null, invalid: Boolean}} `value` null quand il
 *   n'y a rien à écrire
 */
export function parseStock(rawValue) {
	if (
		rawValue === undefined ||
		rawValue === null ||
		rawValue.toString().trim() === ''
	) {
		return { value: null, invalid: false };
	}
	if (typeof rawValue === 'number') {
		return Number.isInteger(rawValue) && rawValue >= 0
			? { value: rawValue, invalid: false }
			: { value: null, invalid: true };
	}
	if (typeof rawValue !== 'string') {
		return { value: null, invalid: true };
	}
	// Espaces retirés partout, espaces insécables compris : « 1 200 » est un
	// séparateur de milliers, pas deux nombres.
	const cleaned = rawValue.replace(/\s/g, '');
	const match = /^(\d+)(?:[.,]0+)?$/.exec(cleaned);
	if (!match) {
		return { value: null, invalid: true };
	}
	return { value: parseInt(match[1], 10), invalid: false };
}

/**
 * Ce qu'une cellule Stock ajoute à la ligne envoyée au serveur.
 *
 * Cellule vide : aucune clé, le serveur ne touche alors pas au stock. Toute
 * valeur acceptée par parseStock, 0 compris, part sous la clé `stock`. Une
 * date posée par le tableur a sa propre clé de message, le reste des valeurs
 * refusées le message générique.
 * @param {*} rawValue
 * @returns {{fields: Object, error: String|null}} `error` est une clé I18n
 */
export function readStockCell(rawValue) {
	if (rawValue instanceof Date) {
		return { fields: {}, error: 'importCatalog.error_stock_is_date' };
	}
	const stock = parseStock(rawValue);
	if (stock.invalid) {
		return { fields: {}, error: 'importCatalog.error_invalid_stock' };
	}
	return {
		fields: stock.value === null ? {} : { stock: stock.value },
		error: null,
	};
}

/**
 * Faut-il proposer de supprimer la carte avant l'import ?
 *
 * Oui seulement si le fichier porte une colonne ID **entièrement vide** —
 * y compris un fichier sans aucune ligne, cas extrême du même verdict —, sur
 * une carte non vide et là où le réimport est permis (ni master, ni fils de
 * master, ni zone). Un seul identifiant renseigné suffit à dire que
 * l'utilisateur travaille sur la carte en place : on ne lui propose alors
 * surtout pas de la supprimer. Le serveur revérifie ces mêmes conditions.
 * @param {Object} params
 * @param {Boolean} params.idColumnPresent
 * @param {Array<*>} params.ids - cellules de la colonne ID, une par ligne
 * @param {Boolean} params.catalogEmpty
 * @param {Boolean} params.isMasterReimport
 * @param {Boolean} params.isChild
 * @param {Boolean} params.isZone
 * @returns {Boolean}
 */
export function shouldOfferCatalogWipe({
	idColumnPresent,
	ids,
	catalogEmpty,
	isMasterReimport,
	isChild,
	isZone,
}) {
	if (!idColumnPresent || catalogEmpty || isMasterReimport || isChild || isZone) {
		return false;
	}
	return (ids || []).every(
		id => id === undefined || id === null || id.toString().trim() === ''
	);
}

/**
 * Un fichier sans aucune ligne doit-il proposer le vidage de la carte ?
 *
 * Même verdict que shouldOfferCatalogWipe sur une colonne ID sans cellule,
 * sauf si l'utilisateur a déjà refusé la proposition pour ce fichier : le
 * refus tient jusqu'au prochain chargement, un simple changement de mapping
 * ne la rouvre pas.
 * @param {Object} params
 * @param {Boolean} params.wipeDeclined
 * @param {Boolean} params.idColumnPresent
 * @param {Boolean} params.catalogEmpty
 * @param {Boolean} params.isMasterReimport
 * @param {Boolean} params.isChild
 * @param {Boolean} params.isZone
 * @returns {Boolean}
 */
export function shouldOfferEmptyFileWipe({ wipeDeclined, ...context }) {
	if (wipeDeclined) {
		return false;
	}
	return shouldOfferCatalogWipe({ ...context, ids: [] });
}

export function parsePrice(rawValue) {
	if (typeof rawValue === 'number') {
		return Number.isFinite(rawValue) ? rawValue : null;
	}
	if (!rawValue) {
		return null;
	}
	const cleaned = rawValue
		.toString()
		.replace(/[€\s]/g, '')
		.replace(',', '.');
	const parsed = parseFloat(cleaned);
	return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Best-effort heuristic: does this product name look like a drink?
 * Mirrors the same heuristic used server-side in
 * carte-web-server/inc/dao/ProductCatalogImport.js — kept independent
 * (small, self-contained list) since client and server are separate
 * deployables; the server is the source of truth for the actual insert,
 * this is only used for the client-side preview.
 * @param {String} name
 * @returns {Boolean}
 */
export function isDrink(name) {
	const normalized = normalize(name);
	if (VOLUME_UNIT_PATTERN.test(normalized)) {
		return true;
	}
	return DRINK_KEYWORDS.some(keyword => {
		const pattern = new RegExp(`\\b${escapeRegExp(normalize(keyword))}\\b`);
		return pattern.test(normalized);
	});
}

/**
 * Detects a column whose every non-empty value matches one of `entries`'
 * names (e.g. product_type, tax_rate or place_send names) — a single
 * global column, like detectNameColumn/detectPriceColumn, not a per-row
 * scan. Returns null if no column qualifies (no column, or any
 * non-matching non-empty value disqualifies it — silent, high-confidence
 * detection only).
 * @param {Array<Array<*>>} rows - data rows (headers excluded)
 * @param {Number} columnCount
 * @param {Array<{id: (Number|String), name: String}>} entries
 * @returns {Number|null}
 */
export function detectColumnByValues(rows, columnCount, entries) {
	// Some entity tables (e.g. category.name) allow a NULL name — guard
	// against it here the same way the server already does (`if (row.name)`
	// in ProductCatalogImport.js) instead of crashing on normalize(null).
	const normalizedNames = new Set(
		entries.filter(entry => entry.name).map(entry => normalize(entry.name))
	);
	let bestColumn = null;
	let bestScore = 0;

	for (let col = 0; col < columnCount; col++) {
		let nonEmpty = 0;
		let matches = 0;
		rows.forEach(row => {
			const value = row[col];
			if (value !== undefined && value !== null && value.toString().trim() !== '') {
				nonEmpty++;
				if (normalizedNames.has(normalize(value))) {
					matches++;
				}
			}
		});
		if (nonEmpty > 0 && matches === nonEmpty && matches > bestScore) {
			bestScore = matches;
			bestColumn = col;
		}
	}

	return bestColumn;
}

/**
 * Comma-joined display list of `entries`' names, skipping any entry with
 * a falsy name (e.g. category.name is nullable) — used for tips/expected-
 * values hints, where a bare "null" string must never show up.
 * @param {Array<{name: String}>} entries
 * @returns {String}
 */
export function displayableNames(entries) {
	return entries
		.filter(entry => entry.name)
		.map(entry => entry.name)
		.join(', ');
}

/**
 * Resolves a raw Excel cell value to a matching entry (product_type,
 * tax_rate or place_send), or null if it doesn't match (case/accent-
 * insensitive exact match).
 * @param {*} rawValue
 * @param {Array<{id: (Number|String), name: String}>} entries
 * @returns {{id: (Number|String), name: String}|null}
 */
export function matchByName(rawValue, entries) {
	if (rawValue === undefined || rawValue === null || rawValue.toString().trim() === '') {
		return null;
	}
	const normalized = normalize(rawValue);
	return (
		entries.find(entry => entry.name && normalize(entry.name) === normalized) ||
		null
	);
}
